import React, { useState, useContext, useCallback, FC, useMemo } from 'react';
import { connect } from "react-redux"
import {
  Image,
  Button,
  Grid,
  Divider,
  Modal,
  Message,
} from "semantic-ui-react";
import { toast } from 'react-toastify';
import NumberInput from 'semantic-ui-react-numberinput';
import styled from "styled-components";

import { ResponseManagement } from "../../../API";
import Utility from "../../../Utility";
import GlobalContext from "../../../contexts/GlobalContext";
import { RootDispatch, RootState } from "../../../store/index";
import useResponsive from "../../../hooks/useResponsive";

import AmountProvider from "../../../components/Provider/AmountProvider";
import OptionDetails from "../../../components/Bookings/BookingsSupplements/OptionDetails";
import ExtraDetails from "../../../components/Bookings/BookingsSupplements/ExtraDetails";

const mapProps = (state: RootState) => ({
  bookingFolder: state.booking.bookingFolder,
  extraStock: state.supplement.extraStock,
})

const mapDispatch = (dispatch: RootDispatch) => ({
  handleRedirectType: dispatch.authentication.handleRedirectType,
  updateBookings: dispatch.booking.updateBookings,
  updateBookingsAsync: dispatch.booking.updateBookingsAsync,
  loadSupplement: dispatch.supplement.load,
  addSupplementAsync: dispatch.supplement.addSupplementAsync,
});

type Props = {
  booking: any;
  supplement: any;
  toCloseDetail?: boolean;
  fromModal?: boolean;
  lastItem?: boolean;
  setDetailsMode?: any;
} & ReturnType<typeof mapProps> &
  ReturnType<typeof mapDispatch>;

const BookingsSupplement: FC<Props> = ({
  booking,
  supplement,
  bookingFolder,
  updateBookings,
  updateBookingsAsync,
  extraStock,
  loadSupplement,
  setDetailsMode,
  addSupplementAsync,
  ...props
}) => {
  const [globalContext] = useContext(GlobalContext) as any;
  const { isTabletOrMobile } = useResponsive();
  const [quantityState, setQuantityState] = useState<number>(1);
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleChangeSelect = (quantity) => setQuantityState(parseInt(quantity));

  const handleClickSupplement = () => {
    if (props.fromModal) {
      setDetailsMode({ supplement, quantity: quantityState });
    } else {
      props.handleRedirectType(() => setOpenModalConfirm(true));
    }
  };

  const addSupplement = useCallback(() => {
    setLoading(true);

    addSupplementAsync({
      bookingId: supplement.bookingId,
      serviceId: supplement.id,
      serviceType: supplement.type,
      quantity: quantityState,
    })
      .then((response) => {
        ResponseManagement(
          response,
          () => {
            if (response.data.success) {
              setOpenModalConfirm(false);
              toast.success(globalContext.translations.successAddSupplement);
              setQuantityState(1);
            }
          },
          () => {
            setOpenModalConfirm(false);
            toast.error(
              globalContext.translations.failedAddSupplement +
                ": " +
                response.data.message
            );
          }
        );
      })
      .catch((error) => {
        setOpenModalConfirm(false);
        toast.error(globalContext.translations.failedAddSupplement);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [supplement, bookingFolder, quantityState]);

  const stockExtra = useMemo(() => {
    let disabled = false;
    let stock =
      extraStock?.[booking.arrivalDate]?.[supplement?.id]?.availability ?? 99;
    if (stock < 1) {
      disabled = true;
      stock = 0;
    }

    return { stock, disabled };
  }, [extraStock, booking, supplement]);

  return (
    <>
      <div>
        <Grid style={{ height: "100%" }} columns="equal" centered padded>
          <Grid.Column
            textAlign="center"
            computer={props.fromModal ? 6 : 7}
            tablet={8}
            mobile={14}
          >
            <Image ui centered src={Utility.imageExists(supplement.image)} />
          </Grid.Column>
          <Grid.Column
            style={{ margin: "auto" }}
            computer={props.fromModal ? 10 : 9}
            tablet={8}
            mobile={14}
          >
            {supplement.type === "extra" &&
              globalContext.displayControl.addExtraEarlyCheckIn && (
                <>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      {props.fromModal ? (
                        <h3>{supplement.name}</h3>
                      ) : (
                        <h4>{supplement.name}</h4>
                      )}
                    </div>
                    <div>
                      <h5>
                        {globalContext.translations.total}:{" "}
                        <AmountProvider
                          amount={supplement.price * quantityState}
                          currency={supplement.currency}
                        />
                      </h5>
                    </div>
                  </div>
                  <p className="mt-2 description-text">
                    {supplement.description.shorted()}
                  </p>
                  {!stockExtra.disabled && (
                    <div
                      className="pt-4"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div className="option-extra-select">
                        <NumberInput_
                          size="mini"
                          maxLength={5}
                          disabled={stockExtra.disabled}
                          minValue={0}
                          maxValue={stockExtra.stock}
                          value={
                            stockExtra.disabled ? "0" : quantityState.toString()
                          }
                          onChange={handleChangeSelect}
                        />
                      </div>
                      <div>
                        <Button
                          size="mini"
                          onClick={handleClickSupplement}
                          positive
                          floated="right"
                          icon="shopping basket"
                          labelPosition="left"
                          content={globalContext.translations.detailsAndAdd}
                        />
                      </div>
                    </div>
                  )}
                  {stockExtra.disabled && (
                    <Message size="mini" negative>
                      <p>{globalContext.translations.emptyStockSupplement}</p>
                    </Message>
                  )}
                </>
              )}
            {supplement.type === "option" &&
              globalContext.displayControl.addOptionEarlyCheckIn && (
                <>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      {props.fromModal ? (
                        <h3>{supplement.name}</h3>
                      ) : (
                        <h4>{supplement.name}</h4>
                      )}
                    </div>
                    <div>
                      <h5>
                        {globalContext.translations.total}:{" "}
                        <AmountProvider
                          amount={supplement.price.toString()}
                          currency={supplement.currency}
                        />
                      </h5>
                    </div>
                  </div>
                  <p className="my-2 description-text">
                    {supplement.description.shorted()}
                  </p>
                  <div className="pt-1">
                    <div
                      className="pt-4"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <Button
                          size="mini"
                          onClick={handleClickSupplement}
                          positive
                          floated="right"
                          icon="shopping basket"
                          labelPosition="left"
                          content={globalContext.translations.detailsAndAdd}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
          </Grid.Column>
        </Grid>
        {props.fromModal && !props.lastItem && isTabletOrMobile && <Divider />}
      </div>
      <Modal
        open={openModalConfirm}
        onClose={() => setOpenModalConfirm(false)}
        closeOnDimmerClick={false}
      >
        <Modal.Header>
          {globalContext.translations.confirmAddSupplement} fff
        </Modal.Header>
        <Modal.Content>
          <div style={{ height: "100%", overflow: "auto", maxHeight: "70vh" }}>
            {supplement.type === "extra" && (
              <ExtraDetails
                supplement={supplement}
                quantity={quantityState}
                currency={booking.currency}
              />
            )}
            {supplement.type === "option" && (
              <OptionDetails supplement={supplement} booking={booking} />
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            negative
            onClick={() => setOpenModalConfirm(false)}
            disabled={loading}
          >
            {globalContext.translations.close}
          </Button>
          <Button
            positive
            onClick={addSupplement}
            disabled={loading}
            loading={loading}
          >
            {globalContext.translations.validate}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

const NumberInput_ = styled(NumberInput)`
  & .ui.mini.input {
    width: 50px;
  }
`;

export default connect(mapProps, mapDispatch)(BookingsSupplement);